<template>
    <div ref="outerElementRef" class="sign-in-toggle-redirect">
        <h5 class="sign-in-toggle-redirect__title">{{ topTitle }}</h5>
        <h4 class="sign-in-toggle-redirect__user-name">{{ caption }}</h4>
        <button 
            id="btnAccountPage" 
            ref="button" 
            class="btn sign-in-toggle-redirect__button" 
            @click="btnHandler"
            @focus="onFocus"
            @blur="onBlur"
        >
            {{ btnTxt }}
        </button>
    </div>
</template>

<script setup lang="ts">
    import { Strings } from "@/locales/types/enums/strings";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { useApplicationStore } from "@/stores/Application";
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { isFizzTV } from "@ca.vmedia.tv/domains";

    const ApplicationStore = useApplicationStore();
    const { openModalSignOut } = ApplicationStore;
    
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isClient,
        getUserLogin: login,
        getUserEmail: email,
    } = storeToRefs(UserInfoStoreModule);

    const emit = defineEmits(['back-to-navigation']);
    const { t } = useI18n();
    const { $navigator } = useNuxtApp();
    const router = useRouter();
    const { setFocused, unsetFocused, outerElementRef } = useFocusedComponents('AccountPage');
    const topTitle: string = t(Strings.SETTINGS_ACCOUNT_TITLE).toString();
    const btnTxt: string  = isClient.value ? t(Strings.SETTINGS_ACCOUNT_BTN_SIGN_OUT).toString() : t(Strings.SETTINGS_ACCOUNT_BTN_SIGN_IN).toString();
    const userName: string | null = isFizzTV ? email.value : login.value;
    const caption: string | null = isClient.value ? userName : t(Strings.SETTINGS_ACCOUNT_GUEST_USER_NAME).toString();
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const button: Ref<HTMLButtonElement | null> = ref(null);

    const goToBack = (): void => {
        emit("back-to-navigation");
    }

    const signIn = (): void => {
        router.push(linkItems.login.routerPath);
    };
    const signOut = (): void => {
        openModalSignOut();
    };
    const btnHandler = (): void =>{
        if (isClient.value) {
            signOut();
        } else {
            signIn();
        }
    };
    const onKeyLeft = (event: KeyboardEvent): void => {
        const element = event.target as HTMLElement;
        if (element.getAttribute("data-type") === "modal-button") {
            return;
        }
        goToBack();
    };
    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:left", onKeyLeft);
            $navigator.on("keypress:back", goToBack);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:left", onKeyLeft);
            $navigator.off("keypress:back", goToBack);
            isRegKeyListeners.value = false;
        }
    };

    const onFocus = (): void => {
        registerKeyListeners();
    };
    const onBlur = (): void => {
        unregisterKeyListeners();
    };

    const setSelfFocused = (): void => {
        if (button.value) {
            button.value.focus();
        }
        
        setFocused();
    };
    const unsetSelfFocused = (): void => {
        unregisterKeyListeners();
        unsetFocused();
    };

    onMounted(() => {
        setSelfFocused();
    });

    onBeforeUnmount(() => {
        unsetSelfFocused();
    });
</script>


<style lang="scss">
    @import url("styles.scss");
</style>